import type { Source } from '@highimpact-frontend/api-types';
import { Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material';
import type { ThresholdBundleFormController } from './threshold-bundle-form.hook';
import {
  nativeThresholdFormulaPreviewProvider,
  type ThresholdFormulaPreviewProvider,
} from './threshold-formula-preview-provider';
import { ThresholdFormulaPreviewPanel } from './ThresholdFormulaPreviewPanel';

// -- type definitions --

export interface ThresholdConfirmationStepProps {
  controller: ThresholdBundleFormController;
  formulaPreviewProvider?: ThresholdFormulaPreviewProvider;
}

// -- functions --

const boundKindToLabel = (value: string): string =>
  value === 'minimum' ? 'Minimum' : 'Maximum';

const toDateOnly = (value: string): string => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleDateString('fr-FR');
};

const formatPeriod = (startValue: string, endValue: string): string => {
  const start = toDateOnly(startValue);
  const end = toDateOnly(endValue);

  if (!start && !end) {
    return 'illimitée';
  }

  if (!start) {
    return `jusqu'au ${end}`;
  }

  if (!end) {
    return `à partir du ${start}`;
  }

  return `${start} - ${end}`;
};

const toPlural = (count: number, singular: string, plural: string): string =>
  count > 1 ? plural : singular;

const getGroupLabel = (groupKey: string, sources: Source[]): string =>
  sources.length === 1 ? sources[0].label : groupKey;

const getSelectedGroups = (
  selectedGroupKeys: readonly string[],
  availableSources: Record<string, Source[]>
): Array<{ key: string; label: string; sources: Source[] }> =>
  selectedGroupKeys.map((groupKey) => ({
    key: groupKey,
    label: getGroupLabel(groupKey, availableSources[groupKey] ?? []),
    sources: availableSources[groupKey] ?? [],
  }));

// -- main component --

export function ThresholdConfirmationStep({
  controller,
  formulaPreviewProvider = nativeThresholdFormulaPreviewProvider,
}: ThresholdConfirmationStepProps) {
  const { formValues } = controller;
  const selectedGroups = getSelectedGroups(
    controller.selectedSourceGroupKeys,
    controller.availableSources
  );
  const selectedSources = selectedGroups.flatMap((group) => group.sources);
  const allSelectedGroupsAreSingleSource =
    selectedGroups.length > 0 &&
    selectedGroups.every((group) => group.sources.length <= 1);
  const activePeriodLabel = formatPeriod(
    formValues.definition.active_from,
    formValues.definition.active_to
  );
  const assignmentPeriodLabel = formatPeriod(
    formValues.assignment.active_from,
    formValues.assignment.active_to
  );

  return (
    <Stack spacing={2.5}>
      <Typography variant="subtitle1" fontWeight={700}>
        Vérification avant création du seuil
      </Typography>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.25}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Nom du seuil
              </Typography>
              <Typography variant="body2">
                {formValues.definition.name || 'Non défini'}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 180 }}>
              <Typography variant="caption" color="text.secondary">
                Période active
              </Typography>
              <Typography variant="body2">{activePeriodLabel}</Typography>
            </Box>
          </Stack>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Type de seuil
            </Typography>
            <Typography variant="body2">
              {formValues.definition.threshold_type || 'Non défini'}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Description
            </Typography>
            <Typography variant="body2">
              {formValues.definition.description || 'Aucune description'}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Type de borne
            </Typography>
            <Typography variant="body2">
              {boundKindToLabel(formValues.definition.bound_kind)}
            </Typography>
          </Box>

          <Divider />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Assignations
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                {allSelectedGroupsAreSingleSource
                  ? `${selectedSources.length} ${toPlural(
                      selectedSources.length,
                      'source sélectionnée',
                      'sources sélectionnées'
                    )}`
                  : `${selectedGroups.length} ${toPlural(
                      selectedGroups.length,
                      'groupe sélectionné',
                      'groupes sélectionnés'
                    )}`}
              </Typography>
              {selectedSources.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Aucune assignation sélectionnée
                </Typography>
              ) : allSelectedGroupsAreSingleSource ? (
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
                  {selectedSources.map((source) => (
                    <Chip
                      key={`${source.measure_point_id}-${source.measure_type_id}`}
                      label={source.label}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              ) : (
                <Stack spacing={1.25} sx={{ mt: 0.5 }}>
                  {selectedGroups.map((group) => (
                    <Box key={group.key}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 0.5 }}
                      >
                        {group.label}
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" gap={1}>
                        {group.sources.map((source) => (
                          <Chip
                            key={`${group.key}-${source.measure_point_id}-${source.measure_type_id}`}
                            label={source.label}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
            <Box sx={{ minWidth: 180 }}>
              <Typography variant="caption" color="text.secondary">
                Période d'assignation
              </Typography>
              {allSelectedGroupsAreSingleSource ? (
                <Typography variant="body2">{assignmentPeriodLabel}</Typography>
              ) : (
                <Stack spacing={0.5}>
                  {selectedGroups.map((group) => (
                    <Typography key={`${group.key}-period`} variant="body2">
                      {`${group.label}: ${assignmentPeriodLabel}`}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        </Stack>
      </Paper>

      <ThresholdFormulaPreviewPanel
        values={formValues}
        sourceBindings={formValues.assignment.source_bindings}
        provider={formulaPreviewProvider}
        boundKind={formValues.definition.bound_kind as 'minimum' | 'maximum'}
        fillColor={formValues.default_style.fill_color}
      />
    </Stack>
  );
}
